import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useToastNotify } from '@opswat/ui';
import { b64DecodeUnicode, clearRedirectCookie } from 'services/authenticator';
import { Apikey, Organization, Sso, User } from 'services/interface';
import { axios, Fetcher, MutationFetcher, SWRConfiguration, SWRKey, useSWRImmutable, useSWRMutation } from 'services/swr';
import { useUser } from 'services/User';

interface CSRFHeader {
    token: string;
    sso: Sso;
}

export const useCSRFHeader = (options?: SWRConfiguration<CSRFHeader | undefined>) => {
    const { t } = useTranslation();
    const { toastNotify } = useToastNotify();
    const { setUser } = useUser();
    
    const csrfKey: SWRKey = { url: '/api/variables', baseURL: process.env.GATSBY_SITE_URL, maxRedirects: 0 };

    const csrfHeaderFetcher: Fetcher<CSRFHeader | undefined, SWRKey> = async ({ url, ...config }) => axios.get(url, config)
        .then(response => {
            const token: string = response.data.match(/METADEFENDER_TOKEN='(.*?)'/)[1];
            const ssoData = response.data.match(/SSO='(.*?)'/)?.[1];
            const sso: Sso = ssoData && JSON.parse(b64DecodeUnicode(ssoData));

            if (sso) {
                setUser(prev => ({ ...prev, ...sso, isLoggedIn: !!sso?.apikey }));
            } else {
                setUser(prev => ({ ...prev, isLoggedIn: false }));
            }

            return { token, sso };
        })
        .catch(error => {
            setUser(prev => ({ ...prev, isLoggedIn: false }));

            if (error?.message !== 'Request aborted') {
                toastNotify({ title: t('Error'), message: t('X-Csrf token not found'), type: 'error', hasClose: true, duration: 4000 });
            }

            throw error;
        }).finally(() => {
            clearRedirectCookie();
        });
    
    return useSWRImmutable(csrfKey, csrfHeaderFetcher, options);
};

export const useApikeyData = (options?: SWRConfiguration<User>) => {
    const { data: csrfData } = useCSRFHeader({ shouldRetryOnError: false });
    const { setUser } = useUser();

    const apikeyKey: SWRKey = useMemo(() => {
        if (csrfData?.token && csrfData?.sso?.apikey) {
            return {
                url: '/api/token/apikey',
                baseURL: process.env.GATSBY_SITE_URL,
                headers: {
                    'X-Csrf': csrfData?.token,
                },
                data: {
                    apikey: csrfData?.sso?.apikey,
                }
            };
        }
    
        return null;
    }, [csrfData]);

    const apikeyFetcher: Fetcher<User, SWRKey> = async ({ url, data, ...config }) => axios.post(url, data, config)
        .then(res => {
            setUser(prev => ({ ...prev, ...res.data }));
            return res.data;
        });

    return useSWRImmutable<Apikey>(apikeyKey, apikeyFetcher, options);
};

export const useSetApiKeyData = () => {
    const cacheKey: SWRKey = { url: '/api/token', baseURL: process.env.GATSBY_SITE_URL };

    const updateApiKey: MutationFetcher<string, SWRKey, Apikey> = async ({ url, ...config }, { arg: payload }) => {
        return axios.put(url, payload, config).then(res => res.data);
    };

    return useSWRMutation(cacheKey, updateApiKey, { revalidate: false });
};

export const useSetOrganizationData = () => {
    const cacheKey: SWRKey = { url: '/api/organization', baseURL: process.env.GATSBY_SITE_URL };

    const updateOrganization: MutationFetcher<string, SWRKey, Organization> = async ({ url, ...config }, { arg: payload }) => {
        return axios.put(url, payload, config).then(res => res.data);
    };

    return useSWRMutation(cacheKey, updateOrganization, { revalidate: false });
};

export const useUserData = (options?: SWRConfiguration<User>) => {
    const { data: csrfData, isLoading: isLoadingCsrfData, isValidating: isValidatingCsrfData } = useCSRFHeader({ shouldRetryOnError: false });
    const { data: apikeyData, isLoading: isLoadingApikeyData, isValidating: isValidatingApikeyData, error, mutate } = useApikeyData(options);

    const hasEmail = !!csrfData?.sso?.email;
    const hasApikey = !!csrfData?.sso?.apikey;
    
    // User is logged in if they have email from SSO
    // Missing apikey when email exists is an error state (handled separately)
    const data: User = {
        ...(csrfData?.sso || {}),
        ...(apikeyData || {}),
        isLoggedIn: hasEmail
    };

    const isProcessing = isLoadingCsrfData || isLoadingApikeyData;
    const isLoading = !csrfData?.sso || isProcessing;
    const isValidating = isValidatingCsrfData || isValidatingApikeyData;
    
    // Detect error state: has email but no apikey
    const missingApikeyError = hasEmail && !hasApikey && !isProcessing;

    return { 
        data, 
        isLoading, 
        isValidating, 
        isProcessing, 
        error: missingApikeyError ? new Error('Apikey is missing') : error || null, 
        mutate 
    };
};
